import getSceneObjectMotionEditJob from '@cartwhl/common/api/getSceneObjectMotionEditJob';
import getSceneObjectMotionEdits from '@cartwhl/common/api/getSceneObjectMotionEdits';
import type { MotionEditorJobResponse } from '@cartwhl/common/api/postSceneObjectMotionEdit';
import postSceneObjectMotionEditApply from '@cartwhl/common/api/postSceneObjectMotionEditApply';
import postSceneObjectMotionKeyPoseEdit from '@cartwhl/common/api/postSceneObjectMotionKeyPoseEdit';
import type { MotionKeyPoseStatePayload } from '@cartwhl/common/api/postSceneObjectMotionKeyPoseEdit';
import type { SceneTimelineKeyPoseFromAPI } from '@cartwhl/common/types';

import type { MotionEditorStore } from '../motionEditorStore';
import { normalizeHistoryJobKeyPoses } from './keyPoseApiPayload';
import {
  createGenerationHistoryEntryFromJob,
  getGenerationHistoryStatusForJob,
  getGenerationPhaseForHistoryRecovery,
  getGenerationPhaseForJobStatus,
  isRecoverableGenerationHistoryStatus,
  mapMotionEditJobsToHistoryEntries,
  sortGenerationHistoryEntries,
} from './keyPoseGenerationHistory';
import {
  createKeyPoseGenerationRunId,
  getErrorMessage,
  wait,
} from './keyPoseStoreUtils';
import type {
  KeyPoseGenerationHistoryEntry,
  KeyPoseGenerationPhase,
  KeyPoseState,
} from './types';

const KEY_POSE_GENERATION_POLL_INTERVAL_MS = 1500;

export type KeyPoseGenerationActionDeps = {
  onGenerationHistoryChanged?: () => void;
  parentStore: MotionEditorStore;
  state: KeyPoseState;
};

export type RegenerateSceneObjectMotionKeyPosesInput = {
  sceneID: string;
  referenceName: string;
  motionID: string;
  timelineIndex: number;
  keyPoses: MotionKeyPoseStatePayload;
  apiKey: string;
  prompt?: string;
  duration?: number;
  sampler?: string;
  seed?: number | null;
  signal?: AbortSignal;
};

export type RegenerateSceneObjectMotionKeyPosesResult =
  | { ok: true; job: MotionEditorJobResponse }
  | { ok: false; error: string };

export type RecoverSceneObjectMotionKeyPoseGenerationInput = {
  entry: KeyPoseGenerationHistoryEntry;
  apiKey: string;
  signal?: AbortSignal;
};

export type ApplyHistoryGenerationToSceneInput = {
  entry: KeyPoseGenerationHistoryEntry;
  apiKey: string;
  /** When false, applies motion only and skips fetching history key poses. */
  loadKeyPoses?: boolean;
  signal?: AbortSignal;
};

export type GetHistoryGenerationBvhURLInput = {
  entry: KeyPoseGenerationHistoryEntry;
  apiKey: string;
  signal?: AbortSignal;
};

export type GetHistoryGenerationKeyPosesInput = {
  entry: KeyPoseGenerationHistoryEntry;
  apiKey: string;
  signal?: AbortSignal;
};

export type KeyPoseGenerationActionResult =
  | { ok: true; job: MotionEditorJobResponse }
  | { ok: false; error: string };

export type ApplyHistoryGenerationToSceneResult =
  | {
      ok: true;
      job: MotionEditorJobResponse;
      keyPoses: SceneTimelineKeyPoseFromAPI[];
    }
  | { ok: false; error: string };

type GetHistoryGenerationKeyPosesResult =
  | {
      ok: true;
      job: MotionEditorJobResponse;
      keyPoses: SceneTimelineKeyPoseFromAPI[];
    }
  | { ok: false; error: string };

export type LoadSceneObjectMotionEditHistoryInput = {
  sceneID: string;
  referenceName: string;
  motionID: string;
  timelineIndex: number;
  apiKey: string;
  signal?: AbortSignal;
  shouldApplyResult?: () => boolean;
};

type WaitForSceneObjectMotionEditJobInput = {
  apiKey: string;
  initialJob: MotionEditorJobResponse;
  referenceName: string;
  runId: string;
  sceneID: string;
  signal?: AbortSignal;
  timelineIndex: number;
};

export async function loadSceneObjectMotionEditHistory(
  deps: KeyPoseGenerationActionDeps,
  {
    sceneID,
    referenceName,
    motionID,
    timelineIndex,
    apiKey,
    signal,
    shouldApplyResult,
  }: LoadSceneObjectMotionEditHistoryInput,
) {
  const { data, error } = await getSceneObjectMotionEdits(
    sceneID,
    referenceName,
    timelineIndex,
    apiKey,
    signal,
  );

  if (signal?.aborted || shouldApplyResult?.() === false) {
    return false;
  }

  if (error || !data) {
    deps.state.generationHistory = [];
    deps.onGenerationHistoryChanged?.();
    return false;
  }

  deps.state.generationHistory = mapMotionEditJobsToHistoryEntries({
    currentJobID: getCurrentTimelineJobID(deps, referenceName, timelineIndex),
    jobs: data.jobs,
    motionID,
    referenceName,
    sceneID,
    timelineIndex,
  });
  deps.onGenerationHistoryChanged?.();

  return true;
}

export function clearGenerationHistory({
  onGenerationHistoryChanged,
  state,
}: KeyPoseGenerationActionDeps) {
  state.generationHistory = [];
  onGenerationHistoryChanged?.();
}

export async function regenerateSceneObjectMotionKeyPoses(
  deps: KeyPoseGenerationActionDeps,
  {
    sceneID,
    referenceName,
    motionID,
    timelineIndex,
    keyPoses,
    apiKey,
    prompt,
    duration,
    sampler,
    seed,
    signal,
  }: RegenerateSceneObjectMotionKeyPosesInput,
): Promise<RegenerateSceneObjectMotionKeyPosesResult> {
  const { state } = deps;
  if (state.generation.status === 'pending') {
    return { ok: false, error: 'Regeneration is already in progress.' };
  }

  const runId = createKeyPoseGenerationRunId(referenceName, motionID);
  startGeneration(state, runId);

  const { data, error } = await postSceneObjectMotionKeyPoseEdit(
    sceneID,
    referenceName,
    timelineIndex,
    {
      prompt,
      keyPoses,
      duration,
      sampler,
      seed,
    },
    apiKey,
    signal,
  );

  if (error || !data) {
    const errorMessage = getErrorMessage(error, 'Failed to regenerate motion.');
    failGeneration(state, runId, errorMessage);
    return { ok: false, error: errorMessage };
  }

  upsertGenerationHistoryEntry(
    deps,
    createGenerationHistoryEntryFromJob({
      job: data,
      motionID,
      referenceName,
      sceneID,
      timelineIndex,
    }),
  );

  const completedJob = await waitForSceneObjectMotionEditJob(deps, {
    apiKey,
    initialJob: data,
    referenceName,
    runId,
    sceneID,
    signal,
    timelineIndex,
  });
  if (!completedJob) {
    return {
      ok: false,
      error: state.generation.error ?? 'Failed to regenerate motion.',
    };
  }

  if (!completedJob.outputBvhURL) {
    const errorMessage = 'Regeneration did not return a BVH URL.';
    updateGenerationHistoryEntry(deps, completedJob.jobID, {
      errorMessage,
      status: 'failed',
      updatedAt: new Date().toISOString(),
    });
    failGeneration(state, runId, errorMessage);
    return { ok: false, error: errorMessage };
  }

  const completedEntry = upsertGenerationHistoryEntry(
    deps,
    createGenerationHistoryEntryFromJob({
      job: completedJob,
      motionID,
      referenceName,
      sceneID,
      timelineIndex,
    }),
  );
  const applyResult = await applyGeneratedMotionToScene(deps, {
    apiKey,
    entry: completedEntry,
    runId,
    signal,
  });

  if (!applyResult.ok) {
    return applyResult;
  }

  finishGeneration(state, runId, completedJob);
  return { ok: true, job: completedJob };
}

export async function recoverSceneObjectMotionKeyPoseGeneration(
  deps: KeyPoseGenerationActionDeps,
  { entry, apiKey, signal }: RecoverSceneObjectMotionKeyPoseGenerationInput,
): Promise<KeyPoseGenerationActionResult> {
  const { state } = deps;
  if (state.generation.status === 'pending') {
    return { ok: false, error: 'Regeneration is already in progress.' };
  }

  if (!isRecoverableGenerationHistoryStatus(entry.status)) {
    return {
      ok: false,
      error: 'Regeneration history entry is not recoverable.',
    };
  }

  const runId = createKeyPoseGenerationRunId(
    entry.referenceName,
    entry.motionID,
  );
  startGeneration(
    state,
    runId,
    getGenerationPhaseForHistoryRecovery(entry.status),
  );

  const { data, error } = await getSceneObjectMotionEditJob(
    entry.sceneID,
    entry.referenceName,
    entry.timelineIndex,
    entry.jobID,
    apiKey,
    signal,
  );

  if (error || !data) {
    const errorMessage = getErrorMessage(
      error,
      'Could not recover regeneration job.',
    );
    updateGenerationHistoryEntry(deps, entry.jobID, {
      errorMessage,
      status: 'failed',
      updatedAt: new Date().toISOString(),
    });
    failGeneration(state, runId, errorMessage);
    return { ok: false, error: errorMessage };
  }

  const completedJob = await waitForSceneObjectMotionEditJob(deps, {
    apiKey,
    initialJob: data,
    referenceName: entry.referenceName,
    runId,
    sceneID: entry.sceneID,
    signal,
    timelineIndex: entry.timelineIndex,
  });

  if (!completedJob) {
    return {
      ok: false,
      error: state.generation.error ?? 'Failed to recover regeneration job.',
    };
  }

  if (!completedJob.outputBvhURL) {
    const errorMessage = 'Regeneration did not return a BVH URL.';
    updateGenerationHistoryEntry(deps, entry.jobID, {
      errorMessage,
      status: 'failed',
      updatedAt: new Date().toISOString(),
    });
    failGeneration(state, runId, errorMessage);
    return { ok: false, error: errorMessage };
  }

  upsertGenerationHistoryEntry(
    deps,
    createGenerationHistoryEntryFromJob({
      job: completedJob,
      motionID: entry.motionID,
      referenceName: entry.referenceName,
      sceneID: entry.sceneID,
      timelineIndex: entry.timelineIndex,
    }),
  );
  finishGeneration(state, runId, completedJob);

  return { ok: true, job: completedJob };
}

export async function applyHistoryGenerationToScene(
  deps: KeyPoseGenerationActionDeps,
  {
    entry,
    apiKey,
    loadKeyPoses = true,
    signal,
  }: ApplyHistoryGenerationToSceneInput,
): Promise<ApplyHistoryGenerationToSceneResult> {
  const { state } = deps;
  if (state.generation.status === 'pending') {
    return { ok: false, error: 'Regeneration is already in progress.' };
  }

  const runId = createKeyPoseGenerationRunId(
    entry.referenceName,
    entry.motionID,
  );
  startGeneration(state, runId, 'updating-timeline');

  let keyPoses: SceneTimelineKeyPoseFromAPI[] = [];
  if (loadKeyPoses) {
    const keyPosesResult = await getHistoryGenerationKeyPoses(deps, {
      apiKey,
      entry,
      signal,
    });
    if (!keyPosesResult.ok) {
      failGeneration(state, runId, keyPosesResult.error);
      return keyPosesResult;
    }
    keyPoses = keyPosesResult.keyPoses;
  }

  const applyResult = await applyGeneratedMotionToScene(deps, {
    apiKey,
    entry,
    runId,
    signal,
  });

  if (!applyResult.ok) {
    return applyResult;
  }

  const job: MotionEditorJobResponse = {
    jobID: entry.jobID,
    status: 'COMPLETED',
    operation: 'keyPoseEdit',
    createdAt: entry.createdAt,
    completedAt: entry.completedAt,
    outputBvhURL: entry.outputBvhURL,
  };
  finishGeneration(state, runId, job);

  return { ok: true, job, keyPoses };
}

export async function getHistoryGenerationBvhURL(
  deps: KeyPoseGenerationActionDeps,
  { entry, apiKey, signal }: GetHistoryGenerationBvhURLInput,
): Promise<{ ok: true; url: string } | { ok: false; error: string }> {
  const { data, error } = await getSceneObjectMotionEditJob(
    entry.sceneID,
    entry.referenceName,
    entry.timelineIndex,
    entry.jobID,
    apiKey,
    signal,
  );

  if (error || !data?.outputBvhURL) {
    return {
      ok: false,
      error: getErrorMessage(error, 'Failed to refresh BVH URL.'),
    };
  }

  updateGenerationHistoryFromJob(deps, data);
  return { ok: true, url: data.outputBvhURL };
}

async function getHistoryGenerationKeyPoses(
  deps: KeyPoseGenerationActionDeps,
  { entry, apiKey, signal }: GetHistoryGenerationKeyPosesInput,
): Promise<GetHistoryGenerationKeyPosesResult> {
  const { data, error } = await getSceneObjectMotionEditJob(
    entry.sceneID,
    entry.referenceName,
    entry.timelineIndex,
    entry.jobID,
    apiKey,
    signal,
  );

  if (error || !data) {
    return {
      ok: false,
      error: getErrorMessage(error, 'Failed to load history key poses.'),
    };
  }

  updateGenerationHistoryFromJob(deps, data);
  const normalized = normalizeHistoryJobKeyPoses(data, {
    allowMissingAsEmpty: shouldRestoreMissingHistoryKeyPosesAsEmpty(entry),
  });
  if (!normalized.ok) {
    return normalized;
  }

  return { ok: true, job: data, keyPoses: normalized.keyPoses };
}

function shouldRestoreMissingHistoryKeyPosesAsEmpty(
  entry: KeyPoseGenerationHistoryEntry,
) {
  return entry.isBaseline || entry.userKeyPoseCount === 0;
}

export function syncGenerationHistoryForCurrentScene(
  deps: KeyPoseGenerationActionDeps,
) {
  const { parentStore, state } = deps;
  const sceneID = parentStore.state.scene.data?.sceneID;
  if (!sceneID) {
    state.generationHistory = [];
    deps.onGenerationHistoryChanged?.();
    return;
  }

  state.generationHistory = state.generationHistory.filter(
    (entry) => entry.sceneID === sceneID,
  );
  deps.onGenerationHistoryChanged?.();
}

function startGeneration(
  state: KeyPoseState,
  runId: string,
  phase: KeyPoseGenerationPhase = 'queued',
) {
  state.generation = {
    status: 'pending',
    phase,
    activeRunId: runId,
    error: null,
  };
}

function setGenerationPhase(
  state: KeyPoseState,
  runId: string,
  phase: KeyPoseGenerationPhase,
) {
  if (state.generation.activeRunId !== runId) {
    return;
  }

  state.generation.phase = phase;
}

function finishGeneration(
  state: KeyPoseState,
  runId: string,
  job: MotionEditorJobResponse,
) {
  if (state.generation.activeRunId !== runId) {
    return;
  }

  state.generation = {
    status: 'complete',
    phase: null,
    activeRunId: null,
    error: null,
    completedJobId: job.jobID,
    outputBvhURL: job.outputBvhURL,
  };
}

function failGeneration(state: KeyPoseState, runId: string, error: string) {
  if (state.generation.activeRunId !== runId) {
    return;
  }

  state.generation = {
    status: 'failed',
    phase: null,
    activeRunId: null,
    error,
  };
}

async function waitForSceneObjectMotionEditJob(
  deps: KeyPoseGenerationActionDeps,
  {
    apiKey,
    initialJob,
    referenceName,
    runId,
    sceneID,
    signal,
    timelineIndex,
  }: WaitForSceneObjectMotionEditJobInput,
) {
  const { state } = deps;
  let job = initialJob;

  for (;;) {
    setGenerationPhase(
      state,
      runId,
      getGenerationPhaseForJobStatus(job.status),
    );
    updateGenerationHistoryFromJob(deps, job);

    if (job.status === 'COMPLETED') {
      return job;
    }

    if (job.status === 'FAILED' || job.status === 'CANCELLED') {
      updateGenerationHistoryEntry(deps, job.jobID, {
        errorMessage:
          job.errorMessage || `Motion edit ${job.status.toLowerCase()}.`,
        status: job.status === 'CANCELLED' ? 'cancelled' : 'failed',
        updatedAt: job.updatedAt ?? new Date().toISOString(),
      });
      failGeneration(
        state,
        runId,
        job.errorMessage || `Motion edit ${job.status.toLowerCase()}.`,
      );
      return null;
    }

    await wait(KEY_POSE_GENERATION_POLL_INTERVAL_MS, signal);
    if (signal?.aborted) {
      updateGenerationHistoryEntry(deps, job.jobID, {
        errorMessage: 'Regeneration was cancelled.',
        status: 'cancelled',
        updatedAt: new Date().toISOString(),
      });
      failGeneration(state, runId, 'Regeneration was cancelled.');
      return null;
    }

    const { data, error } = await getSceneObjectMotionEditJob(
      sceneID,
      referenceName,
      timelineIndex,
      job.jobID,
      apiKey,
      signal,
    );

    if (error || !data) {
      const errorMessage = getErrorMessage(
        error,
        'Failed to poll regeneration job.',
      );
      updateGenerationHistoryEntry(deps, job.jobID, {
        errorMessage,
        status: 'failed',
        updatedAt: new Date().toISOString(),
      });
      failGeneration(state, runId, errorMessage);
      return null;
    }

    job = data;
  }
}

async function applyGeneratedMotionToScene(
  deps: KeyPoseGenerationActionDeps,
  {
    apiKey,
    entry,
    runId,
    signal,
  }: {
    apiKey: string;
    entry: KeyPoseGenerationHistoryEntry;
    runId: string;
    signal?: AbortSignal;
  },
): Promise<{ ok: true } | { ok: false; error: string }> {
  const { parentStore, state } = deps;
  setGenerationPhase(state, runId, 'updating-timeline');
  updateGenerationHistoryEntry(deps, entry.jobID, {
    errorMessage: undefined,
    status: 'updating-timeline',
    updatedAt: new Date().toISOString(),
  });

  const { error: applyError } = await postSceneObjectMotionEditApply(
    entry.sceneID,
    entry.referenceName,
    entry.timelineIndex,
    entry.jobID,
    apiKey,
    signal,
  );

  if (applyError) {
    const errorMessage = getErrorMessage(
      applyError,
      'Failed to update timeline.',
    );
    updateGenerationHistoryEntry(deps, entry.jobID, {
      errorMessage,
      status: 'apply-failed',
      updatedAt: new Date().toISOString(),
    });
    failGeneration(state, runId, errorMessage);
    return { ok: false, error: errorMessage };
  }

  const didRefreshScene = await parentStore.refreshSceneAfterTimelineMutation({
    apiKey,
    referenceName: entry.referenceName,
    sceneID: entry.sceneID,
    timelineIndex: entry.timelineIndex,
  });
  if (!didRefreshScene) {
    const errorMessage = 'Failed to refresh updated timeline.';
    updateGenerationHistoryEntry(deps, entry.jobID, {
      errorMessage,
      status: 'apply-failed',
      updatedAt: new Date().toISOString(),
    });
    failGeneration(state, runId, errorMessage);
    return { ok: false, error: errorMessage };
  }

  const appliedAt = new Date().toISOString();
  updateGenerationHistoryEntry(deps, entry.jobID, {
    errorMessage: undefined,
    status: 'applied',
    updatedAt: appliedAt,
  });
  void loadSceneObjectMotionEditHistory(deps, {
    apiKey,
    motionID: entry.motionID,
    referenceName: entry.referenceName,
    sceneID: entry.sceneID,
    timelineIndex: entry.timelineIndex,
  });

  return { ok: true };
}

function upsertGenerationHistoryEntry(
  deps: KeyPoseGenerationActionDeps,
  entry: KeyPoseGenerationHistoryEntry,
) {
  const { onGenerationHistoryChanged, state } = deps;
  const index = state.generationHistory.findIndex(
    (historyEntry) => historyEntry.jobID === entry.jobID,
  );
  if (index === -1) {
    state.generationHistory = sortGenerationHistoryEntries([
      ...state.generationHistory,
      entry,
    ]);
  } else {
    state.generationHistory[index] = {
      ...state.generationHistory[index],
      ...entry,
    };
    state.generationHistory = sortGenerationHistoryEntries(
      state.generationHistory,
    );
  }
  onGenerationHistoryChanged?.();
  return entry;
}

function updateGenerationHistoryEntry(
  { onGenerationHistoryChanged, state }: KeyPoseGenerationActionDeps,
  jobID: string,
  patch: Partial<Omit<KeyPoseGenerationHistoryEntry, 'jobID'>>,
) {
  const entry = state.generationHistory.find(
    (historyEntry) => historyEntry.jobID === jobID,
  );
  if (!entry) {
    return null;
  }

  Object.assign(entry, patch);
  state.generationHistory = sortGenerationHistoryEntries(
    state.generationHistory,
  );
  onGenerationHistoryChanged?.();
  return entry;
}

function getCurrentTimelineJobID(
  { parentStore }: KeyPoseGenerationActionDeps,
  referenceName: string,
  timelineIndex: number,
) {
  const timelineItem =
    parentStore.state.scene.data?.objects[referenceName]?.timeline[
      timelineIndex
    ];

  return timelineItem?.appliedMotionEditJobID ?? timelineItem?.jobID;
}

function updateGenerationHistoryFromJob(
  deps: KeyPoseGenerationActionDeps,
  job: MotionEditorJobResponse,
) {
  const existingEntry = deps.state.generationHistory.find(
    (entry) => entry.jobID === job.jobID,
  );
  const currentJobID = existingEntry
    ? getCurrentTimelineJobID(
        deps,
        existingEntry.referenceName,
        existingEntry.timelineIndex,
      )
    : undefined;
  const status = getGenerationHistoryStatusForJob(job, currentJobID);
  const patch: Partial<Omit<KeyPoseGenerationHistoryEntry, 'jobID'>> = {
    status,
    updatedAt: job.updatedAt ?? new Date().toISOString(),
  };

  if (job.completedAt) {
    patch.completedAt = job.completedAt;
  }
  if (job.outputBvhURL) {
    patch.outputBvhURL = job.outputBvhURL;
  }
  if (job.thumbnailUrl) {
    patch.thumbnailUrl = job.thumbnailUrl;
  }
  if (job.errorMessage) {
    patch.errorMessage = job.errorMessage;
  }
  if (job.prompt) {
    patch.prompt = job.prompt;
  }
  if (job.userKeyPoseCount !== undefined) {
    patch.userKeyPoseCount = job.userKeyPoseCount;
  }

  updateGenerationHistoryEntry(deps, job.jobID, patch);
}
